import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import { cn } from '@ifixit/ui/lib/utils';
import { useEffect, useId, useState } from 'react';
import type { KeyboardEvent, KeyboardEventHandler } from 'react';
import { type Suggestion, useSuggestions } from './suggest_api';
import { unlessComposing } from './unless_composing';

/**
 * A text input with a suggestion dropdown, following the wiki editor v2
 * metadata search styling: options appear as you type (200ms debounce),
 * mousedown is prevented on rows so the input's blur doesn't close the
 * dropdown before a click lands.
 *
 * The dropdown is a keyboard-navigable combobox: ArrowUp/ArrowDown move the
 * highlight, Enter picks the highlighted option, Escape closes the list.
 * Enter with nothing highlighted falls through to the caller's onKeyDown, so
 * tag-style consumers can still commit typed text.
 *
 * Controlled: the caller owns the text. Picking an option calls onSelect;
 * for name-style fields the caller typically writes the label back into the
 * text, for picker-style fields it clears the text and stores the key.
 */
export function SuggestInput({
   queryKey,
   value,
   onValueChange,
   onSelect,
   fetchSuggestions,
   placeholder,
   ariaLabel,
   excludeKeys = [],
   minQueryLength = 2,
   maxLength,
   className,
   readOnly = false,
   autoFocus = false,
   onKeyDown,
   onBlur,
   listAction,
   searchKey,
   emptyMessage,
   leadingImageUrl,
}: {
   /** Cache-separates this picker from other SuggestInputs on the page. */
   queryKey: string;
   value: string;
   onValueChange: (text: string) => void;
   onSelect: (suggestion: Suggestion) => void;
   fetchSuggestions: (query: string) => Promise<Suggestion[]>;
   placeholder?: string;
   ariaLabel?: string;
   /** Already-chosen entries to hide from the dropdown. */
   excludeKeys?: (number | string)[];
   minQueryLength?: number;
   maxLength?: number;
   className?: string;
   /** readOnly (not disabled) keeps focus through a commit roundtrip. */
   readOnly?: boolean;
   /** For edit-in-place callers that swap this in for rendered text. */
   autoFocus?: boolean;
   /** Commit keys (Enter/comma) and pill-editing keys live with the caller. */
   onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
   /** Fires when the input loses focus, for commit-on-click-off. */
   onBlur?: () => void;
   /** Optional action rendered after the suggestions and included in keyboard navigation. */
   listAction?: { label: string; onSelect: () => void; icon: 'search-plus' | 'search-minus' };
   /** Changing this value reruns the current query, for example after changing search scope. */
   searchKey?: string | number | boolean;
   /**
    * Shown in the dropdown when a query finds nothing. Without it the
    * combobox closes silently on zero matches.
    */
   emptyMessage?: string;
   /**
    * A thumbnail inside the input's left edge, for picker-style fields whose
    * value is a page with an image — the same image its dropdown row showed.
    */
   leadingImageUrl?: string | null;
}) {
   const [isOpen, setIsOpen] = useState(false);
   const [activeIndex, setActiveIndex] = useState(-1);
   const { suggestions, error, isSearching } = useSuggestions(
      queryKey,
      value,
      fetchSuggestions,
      minQueryLength,
      searchKey
   );
   const visible = suggestions.filter(suggestion => !excludeKeys.includes(suggestion.key));
   const listboxId = useId();
   const optionId = (index: number) => `${listboxId}-option-${index}`;
   // A pending search keeps the dropdown open so a slow response doesn't
   // leave the combobox silent after typing.
   const hasSearchQuery = value.trim().length >= minQueryLength;
   const optionCount = visible.length + (listAction ? 1 : 0);
   const showEmptyMessage = emptyMessage !== undefined && !isSearching && visible.length === 0;
   const isExpanded =
      isOpen &&
      hasSearchQuery &&
      (visible.length > 0 || isSearching || listAction !== undefined || showEmptyMessage);
   const activeOptionId =
      isExpanded && activeIndex >= 0 && activeIndex < optionCount
         ? optionId(activeIndex)
         : undefined;

   // Keep the highlighted option in view when arrowing past the visible slice.
   useEffect(() => {
      if (activeOptionId) {
         document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' });
      }
   }, [activeOptionId]);

   const select = (suggestion: Suggestion) => {
      // A browse-style field (minQueryLength 0) lists its whole set on focus
      // and exists to add several in a row. Picking keeps focus in the input,
      // and only a *new* focus reopens the list, so closing here would strand
      // the user: clicking the same input again fires no focus event. Leave it
      // open — the picked option drops out of the list on the next render.
      setIsOpen(minQueryLength === 0);
      setActiveIndex(-1);
      onSelect(suggestion);
   };

   // Wrapped so a composing IME keeps Enter, Escape, and the arrows — for
   // the dropdown here and for every caller-passed commit handler below.
   const handleKeyDown = unlessComposing((event: KeyboardEvent<HTMLInputElement>) => {
      if (isExpanded) {
         if (event.key === 'ArrowDown' && optionCount > 0) {
            event.preventDefault();
            setActiveIndex(index => (index + 1) % optionCount);
            return;
         }
         if (event.key === 'ArrowUp' && optionCount > 0) {
            event.preventDefault();
            setActiveIndex(index => (index <= 0 ? optionCount - 1 : index - 1));
            return;
         }
         if (event.key === 'Enter' && activeIndex >= 0 && activeIndex < optionCount) {
            event.preventDefault();
            if (activeIndex < visible.length) {
               select(visible[activeIndex]);
            } else {
               setActiveIndex(-1);
               listAction?.onSelect();
            }
            return;
         }
         if (event.key === 'Escape') {
            event.preventDefault();
            setIsOpen(false);
            setActiveIndex(-1);
            return;
         }
      }
      // Enter with nothing highlighted, commit/pill-editing keys, etc. stay
      // with the caller.
      onKeyDown?.(event);
   });

   return (
      <div className={className}>
         <div className="relative">
            {leadingImageUrl && (
               <img
                  src={leadingImageUrl}
                  alt=""
                  className="pointer-events-none absolute left-2 top-1/2 size-6 -translate-y-1/2 rounded object-cover"
               />
            )}
            <Input
               id={`${listboxId}-input`}
               type="text"
               autoComplete="off"
               className={cn(leadingImageUrl && 'pl-9')}
               // Every SuggestInput field holds names and identifiers (items,
               // tags, teams, guide titles), not prose; spellcheck squiggles
               // on product names read as errors.
               spellCheck={false}
               autoFocus={autoFocus}
               role="combobox"
               aria-expanded={isExpanded}
               aria-controls={listboxId}
               aria-autocomplete="list"
               aria-activedescendant={activeOptionId}
               value={value}
               placeholder={placeholder}
               aria-label={ariaLabel}
               maxLength={maxLength}
               readOnly={readOnly}
               onChange={event => {
                  setIsOpen(true);
                  // A new query invalidates the old highlight; start each
                  // result set with nothing highlighted.
                  setActiveIndex(-1);
                  onValueChange(event.target.value);
               }}
               // Zero-length pickers use focus as their explicit "list all"
               // action. Other inputs still open only after typing, so
               // focusing a filled item row does not cover its controls.
               onFocus={() => {
                  if (minQueryLength === 0) {
                     setIsOpen(true);
                  }
               }}
               onBlur={() => {
                  setIsOpen(false);
                  onBlur?.();
               }}
               onKeyDown={handleKeyDown}
            />
            {isExpanded && (
               <div
                  id={listboxId}
                  role="listbox"
                  className="absolute left-0 top-full z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg"
               >
                  {visible.map((suggestion, index) => (
                     <button
                        key={suggestion.key}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === activeIndex}
                        type="button"
                        className={cn(
                           'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-brand-50',
                           index === activeIndex && 'bg-brand-50'
                        )}
                        // Keep focus on the input so its onBlur doesn't close
                        // the dropdown before this click lands.
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => select(suggestion)}
                     >
                        {suggestion.imageUrl && (
                           <img
                              src={suggestion.imageUrl}
                              alt=""
                              className="size-6 shrink-0 rounded object-cover"
                           />
                        )}
                        <span className="min-w-0 flex-1">
                           <span className="block truncate">{suggestion.label}</span>
                           {suggestion.description && (
                              <span className="block truncate text-xs text-gray-600">
                                 {suggestion.description}
                              </span>
                           )}
                        </span>
                     </button>
                  ))}
                  {isSearching && visible.length === 0 && (
                     <div aria-live="polite" className="px-3 py-2 text-sm text-gray-500">
                        {_js('Searching...')}
                     </div>
                  )}
                  {showEmptyMessage && (
                     <div aria-live="polite" className="px-3 py-2 text-sm text-gray-500">
                        {emptyMessage}
                     </div>
                  )}
                  {listAction && (
                     <button
                        id={optionId(visible.length)}
                        role="option"
                        aria-selected={activeIndex === visible.length}
                        type="button"
                        className={cn(
                           'flex w-full items-center justify-center gap-2 border-t border-gray-200 px-3 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50',
                           activeIndex === visible.length && 'bg-brand-50'
                        )}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => {
                           setActiveIndex(-1);
                           listAction.onSelect();
                        }}
                     >
                        <FaIcon name={listAction.icon} className="size-3.5" />
                        {listAction.label}
                     </button>
                  )}
               </div>
            )}
         </div>
         {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
   );
}
